import {
  type AdminMessage,
  AdminMessageSchema,
  type ApiError,
  type AvailableScenario,
  AvailableScenarioSchema,
  type BulkResult,
  BulkResultSchema,
  type EvalRunCreate,
  type EvalRunDetail,
  EvalRunDetailSchema,
  type EvalRunSummary,
  EvalRunSummarySchema,
  type EvalSuggestionDetail,
  EvalSuggestionDetailSchema,
  type EvalSuggestionSummary,
  EvalSuggestionSummarySchema,
  type GovernorConfigResponse,
  GovernorConfigResponseSchema,
  type GovernorConfigUpdate,
  type GovernorRejection,
  GovernorRejectionSchema,
  ImprovementSuggestionsSchema,
  type KnowledgeArticleCreate,
  type KnowledgeArticleResponse,
  KnowledgeArticleResponseSchema,
  type KnowledgeArticleRollback,
  type KnowledgeArticleUpdate,
  type KnowledgeArticleVersion,
  KnowledgeArticleVersionSchema,
  type KnowledgeImportResult,
  KnowledgeImportResultSchema,
  type KnowledgeQueryResponse,
  KnowledgeQueryResponseSchema,
  type MessageSearchType,
  type PaginatedThreadsResponse,
  PaginatedThreadsResponseSchema,
  type PersonaCreate,
  type PersonaResponse,
  PersonaResponseSchema,
  type PersonaUpdate,
  type PromptExperimentCreate,
  type PromptExperimentDetail,
  PromptExperimentDetailSchema,
  type PromptExperimentExtendResult,
  PromptExperimentExtendResultSchema,
  type PromptExperimentSuggestNext,
  PromptExperimentSuggestNextSchema,
  type PromptExperimentSummary,
  PromptExperimentSummarySchema,
  type SuggestionBulkRequest,
  type SuggestionCounts,
  SuggestionCountsSchema,
  type SuggestionStatus,
  type SuggestionTrigger,
  type SuggestionUpdate,
  type ThreadFilters,
  type ThreadGradeResponse,
  ThreadGradeResponseSchema,
  type ThreadSummaryResponse,
  ThreadSummaryResponseSchema,
  type ThreadWithMessagesResponse,
  ThreadWithMessagesResponseSchema,
  type TokenEstimateResponse,
  TokenEstimateResponseSchema,
  type UiConfig,
  UiConfigSchema,
} from "@/types/api.types";
import { z } from "zod";

export class AdminApiClient {
  private baseUrl: string;
  private getAuthToken: () => Promise<string | null>;

  constructor(baseUrl: string, getAuthToken: () => Promise<string | null>) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
    this.getAuthToken = getAuthToken;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const token = await this.getAuthToken();
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async handleErrorResponse(response: Response): Promise<never> {
    const error: ApiError = await response.json().catch(() => ({
      code: "UNKNOWN_ERROR",
      message: `Request failed with status ${response.status}`,
    }));
    throw error;
  }

  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    return response.json();
  }

  private async fetchNoContent(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }
  }

  private async fetchRaw(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<Response> {
    const token = await this.getAuthToken();
    const authHeaders: HeadersInit = token
      ? { Authorization: `Bearer ${token}` }
      : {};
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    });

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    return response;
  }

  private validate<T>(schema: z.ZodSchema<T>, data: unknown, label: string): T {
    const result = schema.safeParse(data);
    if (!result.success) {
      console.error(`Validation error for ${label}:`, result.error);
      throw {
        code: "INVALID_RESPONSE",
        message: `Received invalid ${label} response format from server`,
      } as ApiError;
    }
    return result.data;
  }

  // Threads

  async getThreads(
    filters: ThreadFilters = {},
    signal?: AbortSignal,
  ): Promise<PaginatedThreadsResponse> {
    const params = new URLSearchParams();

    if (filters.user_display_name) params.set("user_display_name", filters.user_display_name);
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);
    if (filters.flagged_harmful !== undefined)
      params.set("flagged_harmful", String(filters.flagged_harmful));
    if (filters.has_governor_rejection !== undefined)
      params.set("has_governor_rejection", String(filters.has_governor_rejection));
    if (filters.vote_filter) params.set("vote_filter", filters.vote_filter);
    if (filters.is_test_user !== undefined)
      params.set("is_test_user", String(filters.is_test_user));
    if (filters.hide_empty) params.set("hide_empty", "true");
    if (filters.limit) params.set("limit", filters.limit.toString());
    if (filters.offset) params.set("offset", filters.offset.toString());

    const queryString = params.toString();
    const endpoint = `/admin/threads${queryString ? `?${queryString}` : ""}`;

    const response = await this.fetch<unknown>(endpoint, { signal });
    return this.validate(PaginatedThreadsResponseSchema, response, "threads");
  }

  async getThread(
    threadId: string,
    signal?: AbortSignal,
  ): Promise<ThreadWithMessagesResponse> {
    const response = await this.fetch<unknown>(`/admin/threads/${threadId}`, {
      signal,
    });
    return this.validate(ThreadWithMessagesResponseSchema, response, "thread");
  }

  async summarizeThread(
    threadId: string,
    signal?: AbortSignal,
  ): Promise<ThreadSummaryResponse> {
    const response = await this.fetch<unknown>(
      `/admin/threads/${threadId}/summary`,
      { method: "POST", signal },
    );
    return this.validate(
      ThreadSummaryResponseSchema,
      response,
      "thread summary",
    );
  }

  async getThreadGrade(
    threadId: string,
    signal?: AbortSignal,
  ): Promise<ThreadGradeResponse | null> {
    const response = await this.fetch<unknown>(
      `/admin/threads/${threadId}/grade`,
      { signal },
    );
    if (response === null) return null;
    return this.validate(
      ThreadGradeResponseSchema,
      response,
      "thread grade",
    );
  }

  // Search

  async searchMessages(
    terms: string,
    searchType: MessageSearchType,
    types: string[],
    signal?: AbortSignal,
  ): Promise<AdminMessage[]> {
    const params = new URLSearchParams({ terms, search_type: searchType });
    for (const t of types) params.append('types', t);
    const response = await this.fetch<unknown>(
      `/admin/search?${params.toString()}`,
      { signal },
    );
    return this.validate(z.array(AdminMessageSchema), response, "search");
  }

  // Governor Config

  async getGovernorConfig(signal?: AbortSignal): Promise<GovernorConfigResponse> {
    const response = await this.fetch<unknown>("/admin/governor", { signal });
    return this.validate(
      GovernorConfigResponseSchema,
      response,
      "governor config",
    );
  }

  // Runtime feature flags. Public endpoint (no auth required); any attached
  // bearer token is harmless.
  async getUiConfig(signal?: AbortSignal): Promise<UiConfig> {
    const response = await this.fetch<unknown>("/config/ui", { signal });
    return this.validate(UiConfigSchema, response, "ui config");
  }

  async updateGovernorConfig(
    data: GovernorConfigUpdate,
  ): Promise<GovernorConfigResponse> {
    const response = await this.fetch<unknown>("/admin/governor", {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return this.validate(
      GovernorConfigResponseSchema,
      response,
      "governor config",
    );
  }

  // Governor Rejections

  async flagFalsePositive(
    rejectionId: number,
    note: string,
  ): Promise<GovernorRejection> {
    const response = await this.fetch<unknown>(
      `/admin/rejections/${rejectionId}/false-positive`,
      {
        method: "PATCH",
        body: JSON.stringify({ note }),
      },
    );
    return this.validate(GovernorRejectionSchema, response, "rejection");
  }

  // Personas

  async getPersonas(
    isEnabled?: boolean,
    signal?: AbortSignal,
  ): Promise<PersonaResponse[]> {
    const params = new URLSearchParams();
    if (isEnabled !== undefined) params.set("is_enabled", String(isEnabled));
    const queryString = params.toString();
    const endpoint = `/admin/personas${queryString ? `?${queryString}` : ""}`;

    const response = await this.fetch<unknown>(endpoint, { signal });
    return this.validate(z.array(PersonaResponseSchema), response, "personas");
  }

  async createPersona(data: PersonaCreate): Promise<PersonaResponse> {
    const response = await this.fetch<unknown>("/admin/personas", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return this.validate(PersonaResponseSchema, response, "persona");
  }

  async getPersona(id: number, signal?: AbortSignal): Promise<PersonaResponse> {
    const response = await this.fetch<unknown>(`/admin/personas/${id}`, {
      signal,
    });
    return this.validate(PersonaResponseSchema, response, "persona");
  }

  async updatePersona(
    id: number,
    data: PersonaUpdate,
  ): Promise<PersonaResponse> {
    const response = await this.fetch<unknown>(`/admin/personas/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return this.validate(PersonaResponseSchema, response, "persona");
  }

  async getPersonaTokenEstimate(
    id: number,
    signal?: AbortSignal,
  ): Promise<TokenEstimateResponse> {
    const response = await this.fetch<unknown>(
      `/admin/personas/${id}/token-estimate`,
      { signal },
    );
    return this.validate(
      TokenEstimateResponseSchema,
      response,
      "token estimate",
    );
  }

  // Knowledge

  async getKnowledgeArticles(
    isEnabled?: boolean,
    signal?: AbortSignal,
  ): Promise<KnowledgeArticleResponse[]> {
    const params = new URLSearchParams();
    if (isEnabled !== undefined) params.set("is_enabled", String(isEnabled));
    const queryString = params.toString();
    const endpoint = `/admin/knowledge${queryString ? `?${queryString}` : ""}`;

    const response = await this.fetch<unknown>(endpoint, { signal });
    return this.validate(
      z.array(KnowledgeArticleResponseSchema),
      response,
      "knowledge articles",
    );
  }

  async createKnowledgeArticle(
    data: KnowledgeArticleCreate,
  ): Promise<KnowledgeArticleResponse> {
    const response = await this.fetch<unknown>("/admin/knowledge", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return this.validate(
      KnowledgeArticleResponseSchema,
      response,
      "knowledge article",
    );
  }

  async getKnowledgeArticle(
    id: number,
    signal?: AbortSignal,
  ): Promise<KnowledgeArticleResponse> {
    const response = await this.fetch<unknown>(`/admin/knowledge/${id}`, {
      signal,
    });
    return this.validate(
      KnowledgeArticleResponseSchema,
      response,
      "knowledge article",
    );
  }

  async updateKnowledgeArticle(
    id: number,
    data: KnowledgeArticleUpdate,
  ): Promise<KnowledgeArticleResponse> {
    const response = await this.fetch<unknown>(`/admin/knowledge/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return this.validate(
      KnowledgeArticleResponseSchema,
      response,
      "knowledge article",
    );
  }

  async deleteKnowledgeArticle(id: number): Promise<void> {
    await this.fetchNoContent(`/admin/knowledge/${id}`, { method: "DELETE" });
  }

  async getKnowledgeArticleVersions(
    id: number,
    signal?: AbortSignal,
  ): Promise<KnowledgeArticleVersion[]> {
    const response = await this.fetch<unknown>(
      `/admin/knowledge/${id}/versions`,
      { signal },
    );
    return this.validate(
      z.array(KnowledgeArticleVersionSchema),
      response,
      "knowledge article versions",
    );
  }

  async rollbackKnowledgeArticle(
    id: number,
    data: KnowledgeArticleRollback,
  ): Promise<KnowledgeArticleResponse> {
    const response = await this.fetch<unknown>(
      `/admin/knowledge/${id}/rollback`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.validate(
      KnowledgeArticleResponseSchema,
      response,
      "knowledge article",
    );
  }

  async exportAllKnowledgeArticles(): Promise<Blob> {
    const response = await this.fetchRaw("/admin/knowledge/export");
    return response.blob();
  }

  async exportKnowledgeArticle(
    id: number,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.fetchRaw(`/admin/knowledge/${id}/export`);
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename=(.+)/);
    const filename = match ? match[1] : `article_${id}.md`;
    return { blob: await response.blob(), filename };
  }

  async queryKnowledge(
    query: string,
    signal?: AbortSignal,
  ): Promise<KnowledgeQueryResponse> {
    const response = await this.fetch<unknown>("/admin/knowledge/query", {
      method: "POST",
      body: JSON.stringify({
        query,
        limit: 5
      }),
      signal,
    });
    return this.validate(
      KnowledgeQueryResponseSchema,
      response,
      "knowledge query",
    );
  }

  async importKnowledgeArticles(file: File): Promise<KnowledgeImportResult> {
    const contentType = file.name.endsWith(".zip")
      ? "application/zip"
      : "text/markdown";
    const body = await file.arrayBuffer();
    const response = await this.fetchRaw("/admin/knowledge/import", {
      method: "POST",
      headers: { "Content-Type": contentType },
      body,
    });
    const data = await response.json();
    return this.validate(
      KnowledgeImportResultSchema,
      data,
      "knowledge import result",
    );
  }

  // Eval Runs

  async getSmokeTestScenarios(signal?: AbortSignal): Promise<string[]> {
    const response = await this.fetch<unknown>(
      "/admin/eval-runs/smoke-test/scenarios",
      { signal },
    );
    return this.validate(z.array(z.string()), response, "smoke-test scenarios");
  }

  async getAvailableScenarios(signal?: AbortSignal): Promise<AvailableScenario[]> {
    const response = await this.fetch<unknown>(
      "/admin/eval-runs/scenarios",
      { signal },
    );
    return this.validate(
      z.array(AvailableScenarioSchema),
      response,
      "available scenarios",
    );
  }

  async listEvalRuns(signal?: AbortSignal): Promise<EvalRunSummary[]> {
    const response = await this.fetch<unknown>("/admin/eval-runs", { signal });
    return this.validate(
      z.array(EvalRunSummarySchema),
      response,
      "eval runs",
    );
  }

  async cancelEvalRun(runId: number): Promise<EvalRunSummary> {
    const response = await this.fetch<unknown>(
      `/admin/eval-runs/${runId}/cancel`,
      { method: "POST" },
    );
    return this.validate(EvalRunSummarySchema, response, "eval run");
  }

  async getEvalRun(id: number, signal?: AbortSignal): Promise<EvalRunDetail> {
    const response = await this.fetch<unknown>(`/admin/eval-runs/${id}`, {
      signal,
    });
    return this.validate(EvalRunDetailSchema, response, "eval run");
  }

  async createEvalRun(payload: EvalRunCreate): Promise<EvalRunSummary> {
    const response = await this.fetch<unknown>("/admin/eval-runs", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return this.validate(EvalRunSummarySchema, response, "eval run");
  }

  // Auto-suggested evals

  async listEvalSuggestions(
    params: { status?: SuggestionStatus; trigger_kind?: SuggestionTrigger } = {},
    signal?: AbortSignal,
  ): Promise<EvalSuggestionSummary[]> {
    const qs = new URLSearchParams();
    if (params.status) qs.set("status", params.status);
    if (params.trigger_kind) qs.set("trigger_kind", params.trigger_kind);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const response = await this.fetch<unknown>(
      `/admin/eval-suggestions${suffix}`,
      { signal },
    );
    return this.validate(
      z.array(EvalSuggestionSummarySchema),
      response,
      "eval suggestions",
    );
  }

  async getEvalSuggestionCounts(signal?: AbortSignal): Promise<SuggestionCounts> {
    const response = await this.fetch<unknown>(
      "/admin/eval-suggestions/counts",
      { signal },
    );
    return this.validate(SuggestionCountsSchema, response, "suggestion counts");
  }

  async getEvalSuggestion(
    id: number,
    signal?: AbortSignal,
  ): Promise<EvalSuggestionDetail> {
    const response = await this.fetch<unknown>(
      `/admin/eval-suggestions/${id}`,
      { signal },
    );
    return this.validate(
      EvalSuggestionDetailSchema,
      response,
      "eval suggestion",
    );
  }

  async updateEvalSuggestion(
    id: number,
    payload: SuggestionUpdate,
  ): Promise<EvalSuggestionDetail> {
    const response = await this.fetch<unknown>(
      `/admin/eval-suggestions/${id}`,
      { method: "PATCH", body: JSON.stringify(payload) },
    );
    return this.validate(
      EvalSuggestionDetailSchema,
      response,
      "eval suggestion",
    );
  }

  async bulkEvalSuggestionAction(
    payload: SuggestionBulkRequest,
  ): Promise<BulkResult> {
    const response = await this.fetch<unknown>(
      "/admin/eval-suggestions/bulk",
      { method: "POST", body: JSON.stringify(payload) },
    );
    return this.validate(BulkResultSchema, response, "bulk result");
  }

  async suggestEvalsForThread(
    threadId: string,
  ): Promise<EvalSuggestionSummary[]> {
    const response = await this.fetch<unknown>(
      `/admin/threads/${threadId}/suggest-evals`,
      { method: "POST" },
    );
    return this.validate(
      z.array(EvalSuggestionSummarySchema),
      response,
      "eval suggestions",
    );
  }

  // Download endpoints — return the raw Response so the caller can read the
  // attached filename from Content-Disposition and trigger a browser save.
  async downloadEvalRunCsv(id: number): Promise<Response> {
    return this.fetchRaw(`/admin/eval-runs/${id}/csv`);
  }

  async downloadEvalRunsZip(ids?: number[]): Promise<Response> {
    const qs = ids && ids.length > 0 ? `?ids=${ids.join(",")}` : "";
    return this.fetchRaw(`/admin/eval-runs/csv-zip${qs}`);
  }

  // Prompt Lab

  async listPromptExperiments(signal?: AbortSignal): Promise<PromptExperimentSummary[]> {
    const response = await this.fetch<unknown>("/admin/prompt-lab", { signal });
    return this.validate(z.array(PromptExperimentSummarySchema), response, "experiments");
  }

  async getPromptExperiment(id: number, signal?: AbortSignal): Promise<PromptExperimentDetail> {
    const response = await this.fetch<unknown>(`/admin/prompt-lab/${id}`, { signal });
    return this.validate(PromptExperimentDetailSchema, response, "experiment");
  }

  async createPromptExperiment(
    payload: PromptExperimentCreate,
  ): Promise<PromptExperimentSummary> {
    const response = await this.fetch<unknown>("/admin/prompt-lab", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return this.validate(PromptExperimentSummarySchema, response, "experiment");
  }

  async getPromptLabFocusAreas(signal?: AbortSignal): Promise<string[]> {
    const response = await this.fetch<unknown>("/admin/prompt-lab/focus-areas", {
      signal,
    });
    return this.validate(z.array(z.string()), response, "focus areas");
  }

  /** Short, Claude-generated improvement labels for the New Experiment UI's
   *  suggestion chips, derived from the selected eval's worst-scoring rows. */
  async getImprovementSuggestions(
    sourceEvalRunId: number,
    signal?: AbortSignal,
  ): Promise<string[]> {
    const response = await this.fetch<unknown>(
      `/admin/prompt-lab/improvement-suggestions?source_eval_run_id=${sourceEvalRunId}`,
      { signal },
    );
    return this.validate(ImprovementSuggestionsSchema, response, "suggestions")
      .suggestions;
  }

  async saveIterationAsPersona(
    experimentId: number,
    iterationId: number,
  ): Promise<PromptExperimentSummary> {
    const response = await this.fetch<unknown>(
      `/admin/prompt-lab/${experimentId}/save`,
      {
        method: "POST",
        body: JSON.stringify({ iteration_id: iterationId }),
      },
    );
    return this.validate(PromptExperimentSummarySchema, response, "experiment");
  }

  async cancelPromptExperiment(
    experimentId: number,
  ): Promise<PromptExperimentSummary> {
    const response = await this.fetch<unknown>(
      `/admin/prompt-lab/${experimentId}/cancel`,
      { method: "POST" },
    );
    return this.validate(PromptExperimentSummarySchema, response, "experiment");
  }

  async validateExperiment(
    experimentId: number,
  ): Promise<PromptExperimentSummary> {
    // Triggers a post-hoc full-suite guardrail on a completed experiment.
    // Returns immediately; the guardrail runs in the background and the
    // detail page's existing polling picks up guardrail_run_id when it
    // appears.
    const response = await this.fetch<unknown>(
      `/admin/prompt-lab/${experimentId}/validate`,
      { method: "POST" },
    );
    return this.validate(PromptExperimentSummarySchema, response, "experiment");
  }

  async extendExperiment(
    experimentId: number,
    additionalIterations: number,
  ): Promise<PromptExperimentExtendResult> {
    const response = await this.fetch<unknown>(
      `/admin/prompt-lab/${experimentId}/extend`,
      {
        method: "POST",
        body: JSON.stringify({ additional_iterations: additionalIterations }),
      },
    );
    return this.validate(
      PromptExperimentExtendResultSchema,
      response,
      "extend result",
    );
  }

  async suggestNextExperiment(
    experimentId: number,
  ): Promise<PromptExperimentSuggestNext> {
    const response = await this.fetch<unknown>(
      `/admin/prompt-lab/${experimentId}/suggest-next`,
      { method: "POST" },
    );
    return this.validate(
      PromptExperimentSuggestNextSchema,
      response,
      "suggestion",
    );
  }
}

let apiClientInstance: AdminApiClient | null = null;

export function getAdminApiClient(
  getAuthToken: () => Promise<string | null>,
): AdminApiClient {
  if (!apiClientInstance) {
    const baseUrl =
      import.meta.env.VITE_API_BASE_URL || "http://localhost:8008";
    apiClientInstance = new AdminApiClient(baseUrl, getAuthToken);
  }
  return apiClientInstance;
}

export function resetApiClient(): void {
  apiClientInstance = null;
}
